import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { useUpdaterChainId } from '~/hooks'
import { useV6SDK } from '~/hooks/useV6SDK'
import { useSpotSwapPair, useTokenlonV6NetworkConfig } from '~/state/dex/hooks'
import { useNativeTokenBalance, useTokenBalance } from '~/state/wallet/hooks'
import { useGasLimitScale } from './useGasLimitScale'
import { DEFAULT_GAS_LIMIT, QuoteDTO } from '@tokenlon/v6-sdk'
import { Address, zeroAddress } from 'viem'
import useSWR from 'swr'
import { useSlippage } from './useSlippage'
import { useIsSendByRelayer } from './useIsSendByRelayer'
import { applyGasUsedBuffer } from '~/utils/gasSpeedup'

export const useMaxBtnGasLimit = () => {
  const { address: account } = useAccount()
  const sdk = useV6SDK()
  const [inputToken, outputToken] = useSpotSwapPair()
  const { slippageBPS } = useSlippage()
  const isSendByRelayer = useIsSendByRelayer()

  const nativeTokenBalance = useNativeTokenBalance()
  const inputBalance = useTokenBalance(inputToken)
  const isNativeToken = inputToken?.address === zeroAddress
  const balance = isNativeToken ? nativeTokenBalance : inputBalance
  const amount = balance?.toDecimalsFixed(balance?.decimals)
  const updaterChainId = useUpdaterChainId()

  const {
    defaultGasLimit: networkDefaultGasLimit,
  } = useTokenlonV6NetworkConfig()
  const defaultGasLimit = BigInt(networkDefaultGasLimit || DEFAULT_GAS_LIMIT)
  const gasLimitScale = useGasLimitScale()

  const quoteKey = useMemo(() => {
    // 判断 chainId 是因为切换网络时会改变默认币对，但 input 和 output 没有一起同步更新，会出现 new input 和 old output 同时存在的状态。这里简单判断 token 的 chainId 来阻止发生请求。
    const isInvalidTokens =
      !inputToken?.address ||
      !outputToken?.address ||
      inputToken?.chainId !== updaterChainId ||
      outputToken?.chainId !== updaterChainId

    const isInvalidQuote =
      !account || isInvalidTokens || Number(amount) === 0 || isSendByRelayer

    if (isInvalidQuote) {
      return null
    }

    return [
      'getMaxBtnGasLimit',
      {
        userAddress: account,
        fromToken: inputToken?.address,
        toToken: outputToken?.address,
        amount,
        slippage: slippageBPS,
        chainId: inputToken?.chainId,
        toChainId: outputToken?.chainId,
      } as QuoteDTO,
    ]
  }, [
    account,
    inputToken?.chainId,
    inputToken?.address,
    outputToken?.chainId,
    outputToken?.address,
    inputToken?.chainId,
    outputToken?.chainId,
    amount,
    slippageBPS,
    isSendByRelayer,
  ])

  const fetchGasEstimate = async (_key: string, dto: QuoteDTO) => {
    try {
      const order = await sdk.swap.quote(dto)
      const gasUsed = BigInt(order.gasUsed)
      const gasUsedWithScale = applyGasUsedBuffer(gasUsed)
      let gasLimit: bigint
      if (!gasUsedWithScale) {
        gasLimit = await sdk.swap.estimate({
          order,
          slippage: dto.slippage,
          account: dto.userAddress as Address,
        })
      } else {
        gasLimit = gasUsedWithScale
      }
      return (gasLimit * gasLimitScale) / 100n
    } catch (error) {
      return defaultGasLimit
    }
  }

  const { data: gasLimit } = useSWR(quoteKey, fetchGasEstimate, {
    fallbackData: defaultGasLimit,
    revalidateOnFocus: false, // 禁用焦点重新验证
    dedupingInterval: 1000, // 1秒内的重复请求会被去重
  })

  return gasLimit
}
